<template>
  <div class="grid grid-rows-[1fr_auto] md:grid-rows-[auto_1fr] h-full overflow-y-hidden md:overflow-y-auto w-full">
    <!-- Desktop Header -->
    <div v-if="!isMobile" class="flex-none w-full pe-2 ps-8 pt-4 pb-2 border-gray-100">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-medium text-gray-800">מסמכים ואישורים</h2>
        <Button label="טופס חדש" icon="pi pi-plus" size="small" @click="handleCreateForm" />
      </div>
      <p class="text-sm text-surface-500 mt-1">טפסים שהלקוחות ממלאים וחותמים לפני הטיפול — הסכמה, הצהרות בריאות, אישורים נוספים.</p>
    </div>

    <div class="flex flex-col w-full mb-2 h-full overflow-y-auto px-4 pt-2 md:px-8 md:pt-4">
      <!-- Page description (mobile only; desktop shows it under the header) -->
      <p class="text-sm text-surface-500 mb-4 md:hidden">טפסים שהלקוחות ממלאים וחותמים לפני הטיפול — הסכמה, הצהרות בריאות, אישורים נוספים.</p>

      <!-- Loading State -->
      <div v-if="!dataLoaded" class="flex flex-col w-full h-full items-center justify-center">
        <loader />
      </div>

      <!-- Forms List -->
      <div v-else-if="forms.length > 0" class="w-full flex flex-col md:flex-row md:flex-wrap gap-3 md:gap-4">
        <div
          v-for="form in forms"
          :key="form._id"
          class="bg-white border border-surface-200 rounded-lg p-4 flex items-center gap-4 cursor-pointer hover:border-primary-300 hover:shadow-md transition-all group md:w-[400px]"
          @click="handleSelectForm(form)"
        >
          <div class="w-12 h-12 bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-primary-600 group-hover:text-white transition-colors">
            <i class="pi pi-file text-2xl"></i>
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="font-bold text-surface-800 truncate">{{ form.title }}</h3>
            <p class="text-xs text-surface-500 mt-1 flex items-center gap-1">
              <i class="pi pi-calendar"></i>
              {{ formatDate(form.updatedAt) }}
            </p>
          </div>
          <button class="text-surface-400 hover:text-primary-600 p-2 rounded-full hover:bg-surface-50">
            <i class="pi pi-ellipsis-v text-lg"></i>
          </button>
        </div>
        <div v-if="isMobile" class="h-4 p-2 flex-none"></div>
      </div>

      <!-- Empty State -->
      <div v-else class="w-full h-max flex flex-col justify-center items-center my-auto">
        <i class="pi pi-file text-6xl text-surface-300 mb-4"></i>
        <span class="text-lg font-medium">עדיין לא יצרתם טפסי הסכמה</span>
        <span class="text-center px-4 font-light text-surface-500 mt-1">צרו את הטופס הראשון שלכם עוד היום</span>
        <div
          class="relative py-2 px-4 border border-solid m-2 bg-primary-500 text-white rounded-3xl border-purple-800 flex gap-2 justify-center items-center mt-4 cursor-pointer"
          @click="handleCreateForm"
        >
          <i class="pi pi-plus" />
          <span class="text-sm">יצירת טופס ראשון</span>
        </div>
      </div>
    </div>

    <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { DateTime } from 'luxon';
import type { IConsentForm, IConsentFormAutomation } from '@torim-app/types';
import { ConsentFormsApi } from '@app/api/admin';
import { useAdminStore } from '@app/stores';
import { AdminRouteNames } from '@app/router/route-names';
import { useDrawer } from '@torim-app/shared-components';
import { useHeaderAction } from '@app/composables/header-action';
import { useResponsive } from '@app/composables/responsive';
import { useToast } from 'vue-toastification';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import ConsentFormActions from '@app/components/admin/pages/consent-forms/ConsentFormActions.vue';
import ConsentFormBuilder from '@app/components/admin/pages/consent-forms/ConsentFormBuilder.vue';
import ConsentFormCreator from '@app/components/admin/pages/consent-forms/ConsentFormCreator.vue';
import ConsentFormFiller from '@app/components/admin/pages/consent-forms/ConsentFormFiller.vue';

const adminStore = useAdminStore();
const { setPage } = adminStore;
const { openDrawer, closeDrawer } = useDrawer();
const { setHeaderAction } = useHeaderAction();
const { isMobile } = useResponsive();
const toast = useToast();
const confirm = useConfirm();

const dataLoaded = ref(false);
const forms = ref<IConsentForm[]>([]);

const loadForms = async () => {
  try {
    forms.value = await ConsentFormsApi.fetchConsentForms();
  } catch (e) {
    console.error(e);
    toast.error('שגיאה בטעינת הטפסים');
  } finally {
    dataLoaded.value = true;
  }
};

const formatDate = (date?: Date | string) => {
  if (!date) return '';
  return DateTime.fromISO(date as string).setLocale('he').toLocaleString(DateTime.DATE_MED);
};

const handleCreateForm = () => {
  openDrawer(
    ConsentFormCreator,
    {
      onCreated: (newForm: IConsentForm) => {
        forms.value.unshift(newForm);
        closeDrawer();
        setTimeout(() => openBuilderDrawer(newForm), 350);
      },
      onClose: () => closeDrawer(),
    },
    { title: 'יצירת טופס חדש' },
  );
};

const handleSelectForm = (form: IConsentForm) => {
  openDrawer(
    ConsentFormActions,
    {
      form,
      onEdit: () => {
        closeDrawer();
        setTimeout(() => openBuilderDrawer(form), 350);
      },
      onFill: () => {
        closeDrawer();
        setTimeout(() => openFillerDrawer(form), 350);
      },
      onCopyLink: () => {
        const bid = adminStore.getBusinessSiteData?.bid;
        const link = `${import.meta.env.VITE_BUSINESS_WEBSITE_DOMAIN}/business/${bid}/form/${form._id}`;
        navigator.clipboard.writeText(link);
        toast.success('הקישור הועתק בהצלחה');
        closeDrawer();
      },
      onDelete: () => {
        closeDrawer();
        confirm.require({
          message: 'האם למחוק את הטופס? שימו ❤️ לא ניתן לבטל פעולה זו',
          header: 'רק מוודאים',
          rejectLabel: 'לא',
          acceptLabel: 'כן',
          blockScroll: true,
          accept: async () => {
            try {
              await ConsentFormsApi.deleteConsentForm(form._id!);
              forms.value = forms.value.filter((f) => f._id !== form._id);
              toast.success('הטופס נמחק בהצלחה');
            } catch (e) {
              toast.error('שגיאה במחיקת הטופס');
            }
          },
        });
      },
    },
    { title: form.title, noPadding: true },
  );
};

const openBuilderDrawer = (form: IConsentForm) => {
  openDrawer(
    ConsentFormBuilder,
    {
      form,
      onSave: async (updatedForm: Pick<IConsentForm, 'title' | 'elements'> & { automation?: IConsentFormAutomation }) => {
        try {
          const { automation, ...formData } = updatedForm;
          const saved = await ConsentFormsApi.updateConsentForm(form._id!, formData);
          if (automation) {
            await ConsentFormsApi.updateConsentFormAutomation(form._id!, automation);
            saved.automation = automation;
          }
          const idx = forms.value.findIndex((f) => f._id === form._id);
          if (idx !== -1) forms.value[idx] = saved;
          toast.success('הטופס נשמר בהצלחה');
          closeDrawer();
        } catch (e) {
          toast.error('שגיאה בשמירת הטופס');
        }
      },
      onClose: () => closeDrawer(),
    },
    { title: 'עריכת טופס', full: true, fullMobile: true, noPadding: true },
  );
};

const openFillerDrawer = (form: IConsentForm) => {
  openDrawer(
    ConsentFormFiller,
    {
      form,
      onSubmit: async (submission: {
        customerName: string;
        data: Record<string, any>;
        signatureDataUrl?: string;
      }) => {
        try {
          await ConsentFormsApi.submitConsentForm(form._id!, submission);
          toast.success('הטופס נשלח בהצלחה!');
          closeDrawer();
        } catch (e) {
          toast.error('שגיאה בשליחת הטופס');
        }
      },
      onClose: () => closeDrawer(),
    },
    { title: form.title, full: true, fullMobile: true, noPadding: true },
  );
};

onMounted(() => {
  setPage(AdminRouteNames.ConsentForms);
  // the mobile header "+" replaces the old bottom button
  setHeaderAction({ icon: 'pi-plus', onClick: handleCreateForm, ariaLabel: 'טופס חדש' });
  loadForms();
});
</script>
